import { useEffect, useRef } from "react";

/**
 * TradingView "Symbol Overview" (aka Quick Look) wrapper.
 * - autosizes to the container
 * - exposes onSelect(symbol) so you can sync the dropdown / ticket
 */
export default function QuickLook({
  symbols = [
    "NASDAQ:AAPL",
    "NASDAQ:MSFT",
    "NASDAQ:TSLA",
    "AMEX:SPY",
    "NYSE:VTI",
    "NASDAQ:QQQ",
  ],
  theme = "dark",
  onSelect,
}: {
  symbols?: string[];
  theme?: "light" | "dark";
  onSelect?: (symbol: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // ensure script only once per mount
    const container = ref.current;
    if (!container) return;

    // clear previous
    container.innerHTML = "";

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js";
    script.type = "text/javascript";
    script.async = true;

    // TradingView config
    const tvConfig = {
      symbols: symbols.map((s) => [s]),
      chartOnly: false,
      autosize: true,
      showVolume: true,
      showMA: true,
      locale: "en",
      colorTheme: theme,
      isTransparent: false,
      width: "100%",
      height: 460,
      gridLineColor: "rgba(42, 46, 57, 0)",
      lineWidth: 2,
      scalePosition: "right",
      scaleMode: "Normal",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    };

    script.innerHTML = JSON.stringify(tvConfig);
    const widget = document.createElement("div");
    widget.className = "tradingview-widget-container__widget";
    container.appendChild(widget);
    container.appendChild(script);

    // TradingView doesn’t provide a native click event; we tap the tabs.
    const iv = window.setInterval(() => {
      try {
        const tabs = container.querySelectorAll<HTMLButtonElement>(
          ".tabGroup-CT1 .tab-uTe"
        );
        if (!tabs.length) return;
        tabs.forEach((btn) => {
          // prevent double-binding
          btn.dataset._qlBound ||=
            (btn.addEventListener("click", () => {
              // tab text is like "NASDAQ:AAPL" — push only ticker to parent
              const txt = btn.textContent?.trim() || "";
              const sym = txt.split(":").pop() || txt;
              onSelect?.(sym);
            }),
            "1");
        });
      } catch {}
    }, 500);

    return () => {
      window.clearInterval(iv);
      container.innerHTML = "";
    };
  }, [symbols, theme, onSelect]);

  return (
    <div className="tradingview-widget-container" ref={ref}>
      <div className="tradingview-widget-container__widget" />
    </div>
  );
}
